import Konva from "konva";
import {
  applyWithHistory,
  canEnterStep,
  emptyHistory,
  initialState,
  redo,
  undo,
  type EditorAction,
  type EditorState,
  type History,
  type Step,
} from "../domain/state";
import {
  buildRenderSpec,
  defaultPlacement,
  resolveSlotAssetId,
  type ImageNode,
  type RenderNode,
} from "../domain/renderSpec";
import { clampPlacement } from "../domain/placement";
import { findTemplate, templates } from "../domain/templates";
import type { ImageSlotId, Placement, TemplateDef } from "../domain/types";
import { assetDimensions, getAsset } from "../services/assets";
import { ImageLoadError, loadImageFile } from "../services/imageLoader";
import {
  ModelLoadError,
  onModelProgress,
  preloadModel,
  removeBackground,
} from "../services/backgroundRemoval";
import { exportPng } from "../services/exporter";
import { canShareFile, downloadBlob, shareFile } from "../services/share";
import { browserTracker } from "../../lib/analytics";
import { renderSpecToLayer } from "./konvaRenderer";

const $ = <T extends HTMLElement>(selector: string): T => {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`element not found: ${selector}`);
  return el;
};

export function startEditor(): void {
  const analytics = browserTracker();
  let state: EditorState = initialState;
  let history: History = emptyHistory;
  let outputBlob: Blob | null = null;

  let stage: Konva.Stage | null = null;
  let layer: Konva.Layer | null = null;
  let selectedSlot: ImageSlotId | null = null;
  let selectionOutline: Konva.Rect | null = null;
  // ピンチ・ホイール中の一時配置。確定（undo 1 単位）はジェスチャー終了時に行う
  let gesturePreview: { slot: ImageSlotId; placement: Placement } | null = null;
  let wheelCommitTimer: ReturnType<typeof setTimeout> | null = null;
  let pinchStart: { dist: number; base: Placement; slot: ImageSlotId } | null = null;

  const stepPanels = document.querySelectorAll<HTMLElement>("[data-step-panel]");
  const stepButtons = document.querySelectorAll<HTMLButtonElement>("[data-step-nav]");

  function dispatch(action: EditorAction): void {
    ({ state, history } = applyWithHistory(state, history, action));
    render();
  }

  function goTo(step: Step): void {
    if (!canEnterStep(state, step)) return;
    // 現在ステップのタブを再クリックしても遷移処理は走るため、実際に移動したときだけ計測する
    const moved = state.step !== step;
    // ステップ4進入時はフェイスカムを選択済みにし、選択枠を最初から見せる
    if (step === 4) selectedSlot = "facecam";
    dispatch({ type: "stepChanged", step });
    if (moved) analytics.track("step_view", { step_number: step });
    // タブは狭い画面で横スクロールするため、現在ステップを見える位置に保つ
    document
      .querySelector("[data-step-nav].current")
      ?.scrollIntoView({ block: "nearest", inline: "nearest" });
    if (step === 5) void prepareOutput();
  }

  // ---- ステップ1: 素材取り込み ----

  async function handleFile(slot: ImageSlotId, file: Blob): Promise<void> {
    const errorEl = $(`#${slot}-error`);
    errorEl.textContent = "";
    try {
      const { meta } = await loadImageFile(file);
      dispatch({ type: "imageLoaded", slot, image: meta });
      analytics.track("material_added", { slot });
      if (meta.wasDownscaled) {
        notify(`大きい画像のため端末内で縮小しました（${meta.width}×${meta.height}）`);
      }
      if (slot === "facecam") void runRemoval();
    } catch (e) {
      errorEl.textContent =
        e instanceof ImageLoadError
          ? e.message
          : "画像を読み込めませんでした。別の画像をお試しください。";
    }
  }

  function bindPicker(slot: ImageSlotId): void {
    const input = $<HTMLInputElement>(`#${slot}-input`);
    const zone = $(`#${slot}-zone`);
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file) void handleFile(slot, file);
      input.value = "";
    });
    zone.addEventListener("dragover", (e) => {
      e.preventDefault();
      zone.classList.add("dragover");
    });
    zone.addEventListener("dragleave", () => zone.classList.remove("dragover"));
    zone.addEventListener("drop", (e) => {
      e.preventDefault();
      zone.classList.remove("dragover");
      const file = e.dataTransfer?.files?.[0];
      if (file) void handleFile(slot, file);
    });
  }

  // クリップボード貼り付け（PC のスクリーンショット直後の最短経路）。
  // 未取り込みのスロット（メイン優先）に入れる
  function bindPaste(): void {
    document.addEventListener("paste", (e) => {
      if (state.step !== 1) return;
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/"),
      );
      const file = item?.getAsFile();
      if (!file) return;
      const slot: ImageSlotId = state.main === null ? "main" : "facecam";
      void handleFile(slot, file);
    });
  }

  // ---- ステップ2: 被写体切り抜き ----

  let removalRun = 0;

  async function runRemoval(): Promise<void> {
    if (state.subject === null) return;
    const run = ++removalRun;
    const subjectAssetId = state.subject.assetId;
    dispatch({ type: "removalStatusChanged", status: "loading" });
    try {
      await preloadModel();
      if (run !== removalRun) return;
      dispatch({ type: "removalStatusChanged", status: "running" });
      const resultAssetId = await removeBackground(subjectAssetId);
      if (run !== removalRun) return;
      dispatch({ type: "removalStatusChanged", status: "done", resultAssetId });
      // 切り抜きは裏で走るため、別ステップで作業中でも完了が分かるようにする
      // （結果は自動反映される）
      if (state.step !== 2) notify("切り抜きができました。ステップ2で確認できます");
    } catch (e) {
      if (run !== removalRun) return;
      dispatch({
        type: "removalStatusChanged",
        status: e instanceof ModelLoadError ? "load_failed" : "failed",
      });
      if (state.step !== 2) notify("切り抜きに失敗しました。元画像のまま使えます");
    }
  }

  // 切り抜き結果の採否をプレビューを見て選べるようにする（結果を見ずに判断させない）
  const thumbCache = new Map<string, string>();

  function renderThumbInto(containerId: string, assetId: string | null): void {
    const container = $(containerId);
    if (assetId === null) {
      container.innerHTML = "";
      delete container.dataset.assetId;
      return;
    }
    if (container.dataset.assetId === assetId) return;
    const source = getAsset(assetId);
    if (!source) return;
    let url = thumbCache.get(assetId);
    if (!url) {
      const maxEdge = 240;
      const scale = Math.min(1, maxEdge / Math.max(source.width, source.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(source.width * scale));
      canvas.height = Math.max(1, Math.round(source.height * scale));
      canvas.getContext("2d")?.drawImage(source, 0, 0, canvas.width, canvas.height);
      url = canvas.toDataURL("image/png");
      thumbCache.set(assetId, url);
    }
    container.innerHTML = "";
    const img = document.createElement("img");
    img.src = url;
    img.alt = "";
    container.appendChild(img);
    container.dataset.assetId = assetId;
  }

  function renderRemovalChoice(): void {
    const removedCard = $<HTMLButtonElement>("#choose-removed");
    const originalCard = $<HTMLButtonElement>("#choose-original");
    const removedLabel = $("#removed-label");
    const status = state.removal.status;
    const done = status === "done" && state.removal.resultAssetId !== null;
    const waiting = status === "idle" || status === "loading" || status === "running";

    // 「切り抜きを使う」カードは処理中から見せておき、何が準備されているかを
    // 先に伝える（完了時に突然選択肢が現れる動きにしない）
    removedCard.hidden = state.subject === null;
    removedCard.disabled = !done;
    if (done) {
      renderThumbInto("#removed-thumb", state.removal.resultAssetId);
      removedLabel.textContent = "切り抜きを使う";
    } else {
      const thumb = $("#removed-thumb");
      delete thumb.dataset.assetId;
      thumb.innerHTML = waiting ? '<span class="spinner"></span>' : "";
      removedLabel.textContent = waiting ? "切り抜きを準備中…" : "切り抜きできませんでした";
    }
    renderThumbInto("#original-thumb", state.subject?.assetId ?? null);
    const useOriginal = state.removal.useOriginal || !done;
    removedCard.classList.toggle("selected", done && !useOriginal);
    originalCard.classList.toggle("selected", useOriginal);
  }

  // ---- ステップ3: テンプレート選択 ----

  function renderTemplateList(): void {
    const list = $("#template-list");
    list.innerHTML = "";
    for (const t of templates) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "template-card" + (state.templateId === t.id ? " selected" : "");
      button.dataset.templateId = t.id;
      const thumb = document.createElement("div");
      thumb.className = "template-thumb";
      const name = document.createElement("span");
      name.textContent = t.name;
      button.append(thumb, name);
      // 横型・縦型どちらのテンプレートもサムネイル枠に収める
      const scale = Math.min(288 / t.width, 220 / t.height);
      const miniStage = new Konva.Stage({
        container: thumb,
        width: Math.round(t.width * scale),
        height: Math.round(t.height * scale),
        scale: { x: scale, y: scale },
        listening: false,
      });
      const miniLayer = new Konva.Layer({ listening: false });
      miniStage.add(miniLayer);
      renderSpecToLayer(buildRenderSpec(t, state, assetDimensions), miniLayer, false);
      button.addEventListener("click", () => {
        analytics.track("select_content", { content_type: "template", content_id: t.id });
        dispatch({ type: "templateSelected", templateId: t.id });
        goTo(4);
      });
      list.appendChild(button);
    }
  }

  // ---- ステップ4: レイアウト編集 ----

  function currentTemplate(): TemplateDef | undefined {
    return state.templateId ? findTemplate(state.templateId) : undefined;
  }

  // ジェスチャー中の一時配置を反映した編集状態（描画専用）
  function previewState(): EditorState {
    if (!gesturePreview) return state;
    return {
      ...state,
      edits: {
        ...state.edits,
        [gesturePreview.slot]: {
          ...state.edits[gesturePreview.slot],
          placement: gesturePreview.placement,
        },
      },
    };
  }

  function currentPlacement(slot: ImageSlotId): Placement {
    if (gesturePreview?.slot === slot) return gesturePreview.placement;
    return state.edits[slot]?.placement ?? defaultPlacement;
  }

  // 表示方式に応じた配置の制限。ワイプは枠の露出を防ぎ、立ち絵は倍率のみ制限
  function clampFor(slot: ImageSlotId, placement: Placement): Placement {
    const template = currentTemplate();
    if (!template) return placement;
    const def = template.imageSlots[slot];
    if ((def.display ?? "wipe") === "cutout") {
      return { ...placement, scale: Math.min(Math.max(placement.scale, 0.2), 8) };
    }
    const assetId = resolveSlotAssetId(template, state, slot);
    const dims = assetId ? assetDimensions(assetId) : null;
    if (!dims) return placement;
    return clampPlacement(dims, def.frame, placement);
  }

  function commitGesture(): void {
    if (!gesturePreview) return;
    const { slot, placement } = gesturePreview;
    gesturePreview = null;
    dispatch({ type: "slotEdited", slot, edit: { placement } });
  }

  function zoomSelected(factor: number): void {
    if (!selectedSlot) return;
    const slot = selectedSlot;
    const base = currentPlacement(slot);
    gesturePreview = {
      slot,
      placement: clampFor(slot, { ...base, scale: base.scale * factor }),
    };
    renderStage();
  }

  // 選択の切り替え。ドラッグ開始（ポインタダウン）中に呼ばれるため、
  // レイヤーを再構築せず選択枠の表示だけを更新する
  function selectSlot(slot: ImageSlotId): void {
    if (selectedSlot === slot) return;
    selectedSlot = slot;
    const template = currentTemplate();
    if (template) {
      updateSelectionOutline(buildRenderSpec(template, previewState(), assetDimensions));
    }
  }

  function updateSelectionOutline(spec: RenderNode[]): void {
    if (!layer) return;
    if (!selectionOutline || !selectionOutline.getLayer()) {
      selectionOutline = new Konva.Rect({
        listening: false,
        stroke: "#0e7c86", // site.css の --accent（cyan-700）と揃える
        strokeWidth: 2,
        dash: [8, 6],
        strokeScaleEnabled: false,
      });
      layer.add(selectionOutline);
    }
    const node = spec.find(
      (n): n is ImageNode => n.kind === "image" && n.slot === selectedSlot,
    );
    if (!node) {
      selectionOutline.visible(false);
      return;
    }
    // ワイプは見えている小窓（枠）に、立ち絵は被写体の描画範囲に枠を出す
    const rect = node.display === "cutout" ? node.draw : node.frame;
    selectionOutline.setAttrs({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      visible: true,
    });
    selectionOutline.moveToTop();
  }

  function ensureStage(): void {
    if (stage) return;
    const container = $<HTMLDivElement>("#stage-container");
    stage = new Konva.Stage({ container, width: 320, height: 180 });
    layer = new Konva.Layer();
    stage.add(layer);

    // 拡大縮小: PC はホイール、モバイルは 2 本指ピンチ。
    // 確定はジェスチャー終了時に 1 回（undo 履歴を 1 単位にする）
    stage.on("wheel", (e) => {
      if (state.step !== 4 || !selectedSlot) return;
      e.evt.preventDefault();
      zoomSelected(Math.exp(-e.evt.deltaY * 0.0015));
      if (wheelCommitTimer) clearTimeout(wheelCommitTimer);
      wheelCommitTimer = setTimeout(() => commitGesture(), 400);
    });
    stage.on("touchmove", (e) => {
      const touches = e.evt.touches;
      if (state.step !== 4 || touches.length !== 2) return;
      const t0 = touches[0];
      const t1 = touches[1];
      if (!t0 || !t1) return;
      e.evt.preventDefault();
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      if (!pinchStart) {
        if (!selectedSlot) return;
        pinchStart = { dist, base: currentPlacement(selectedSlot), slot: selectedSlot };
        return;
      }
      const factor = dist / pinchStart.dist;
      gesturePreview = {
        slot: pinchStart.slot,
        placement: clampFor(pinchStart.slot, {
          ...pinchStart.base,
          scale: pinchStart.base.scale * factor,
        }),
      };
      renderStage();
    });
    stage.on("touchend touchcancel", (e) => {
      if (pinchStart && e.evt.touches.length < 2) {
        pinchStart = null;
        commitGesture();
      }
    });
  }

  function renderStage(): void {
    const template = currentTemplate();
    if (!template) return;
    ensureStage();
    if (!stage || !layer) return;

    const container = $("#stage-container");
    const width = container.clientWidth || 320;
    const scale = width / template.width;
    stage.width(width);
    stage.height(Math.round(template.height * scale));
    stage.scale({ x: scale, y: scale });

    const spec = buildRenderSpec(template, previewState(), assetDimensions);
    renderSpecToLayer(spec, layer, true, {
      onPlacementChanged: (slot, placement) =>
        dispatch({ type: "slotEdited", slot, edit: { placement } }),
      onTextSlotTapped: (slot) =>
        $<HTMLInputElement>(slot === "telop" ? "#telop-input" : "#viewers-input").focus(),
      onImageSelected: (slot) => selectSlot(slot),
    });
    // renderSpecToLayer がレイヤーを再構築するため、選択枠は毎回作り直す
    selectionOutline = null;
    updateSelectionOutline(spec);

    const telopInput = $<HTMLInputElement>("#telop-input");
    const viewersInput = $<HTMLInputElement>("#viewers-input");
    telopInput.maxLength = template.textSlots.telop.maxLength;
    if (document.activeElement !== telopInput) {
      telopInput.value = state.edits.telop?.text ?? template.initialTexts.telop;
    }
    if (document.activeElement !== viewersInput) {
      viewersInput.value = String(state.edits.viewers?.value ?? template.initialTexts.viewers);
    }
  }

  // ---- ステップ5: 出力・共有 ----

  async function prepareOutput(): Promise<void> {
    const template = state.templateId ? findTemplate(state.templateId) : undefined;
    if (!template) return;
    const shareBtn = $<HTMLButtonElement>("#share-btn");
    const downloadBtn = $<HTMLButtonElement>("#download-btn");
    shareBtn.hidden = true;
    downloadBtn.disabled = true;
    // 共有はユーザー操作直後の呼び出しが必須のため、この時点で Blob を生成し切っておく
    outputBlob = await exportPng(
      buildRenderSpec(template, state, assetDimensions),
      template.width,
      template.height,
    );
    const preview = $<HTMLImageElement>("#output-preview");
    preview.src = URL.createObjectURL(outputBlob);
    // ステップ5へ入り直すたびに出力を作り直すため、この計測は 1 セッションで複数回発生しうる。
    // 到達数はキーイベントのカウント方法（セッションごとに 1 回）で正規化する
    analytics.track("output_generated", { template_id: template.id });
    downloadBtn.disabled = false;
    shareBtn.hidden = !canShareFile(outputBlob);
    // 共有が使えない環境ではダウンロードが唯一の操作になるため、主要アクションに昇格させる
    downloadBtn.classList.toggle("primary", shareBtn.hidden);
    downloadBtn.classList.toggle("secondary", !shareBtn.hidden);
  }

  // ---- 共通 ----

  function notify(message: string): void {
    const el = $("#notice");
    el.textContent = message;
    el.hidden = false;
    setTimeout(() => {
      el.hidden = true;
    }, 5000);
  }

  function render(): void {
    stepPanels.forEach((panel) => {
      panel.hidden = Number(panel.dataset.stepPanel) !== state.step;
    });
    stepButtons.forEach((button) => {
      const step = Number(button.dataset.stepNav) as Step;
      button.disabled = !canEnterStep(state, step);
      button.classList.toggle("current", step === state.step);
    });

    renderThumbInto("#main-thumb", state.main?.assetId ?? null);
    renderThumbInto("#subject-thumb", state.subject?.assetId ?? null);
    $("#main-status").textContent = state.main ? "タップ / クリックで差し替え" : "未選択";
    $("#subject-status").textContent = state.subject ? "タップ / クリックで差し替え" : "未選択";
    $<HTMLButtonElement>("#to-step-2").disabled = !canEnterStep(state, 2);

    const removalMessages: Record<EditorState["removal"]["status"], string> = {
      idle: "被写体画像を取り込むと自動で切り抜きます",
      loading: "切り抜きの準備中です（初回はモデルの読み込みに時間がかかります）…",
      running: "切り抜き処理中…",
      done: "切り抜きが完了しました。使う画像を選んでください",
      failed: "切り抜きに失敗しました。「切り抜きなしで使う」で続行できます",
      load_failed:
        "切り抜き機能の読み込みに失敗しました（通信状態をご確認ください）。再試行するか、「切り抜きなしで使う」で続行できます",
    };
    $("#removal-status").textContent = removalMessages[state.removal.status];
    $<HTMLButtonElement>("#retry-removal").hidden = state.removal.status !== "load_failed";
    $("#removal-spinner").hidden =
      state.removal.status !== "loading" && state.removal.status !== "running";
    if (state.removal.status !== "loading") $("#removal-progress").hidden = true;
    if (state.step === 2) renderRemovalChoice();

    $<HTMLButtonElement>("#undo-btn").disabled = history.past.length === 0;
    $<HTMLButtonElement>("#redo-btn").disabled = history.future.length === 0;

    if (state.step === 3) renderTemplateList();
    if (state.step === 4) renderStage();
  }

  // ---- 初期化 ----

  bindPicker("main");
  bindPicker("facecam");
  bindPaste();

  document.querySelectorAll<HTMLButtonElement>("[data-step-nav]").forEach((button) => {
    button.addEventListener("click", () => goTo(Number(button.dataset.stepNav) as Step));
  });
  $("#to-step-2").addEventListener("click", () => goTo(2));
  $("#to-step-3").addEventListener("click", () => goTo(3));
  $("#to-step-5").addEventListener("click", () => goTo(5));
  $("#retry-removal").addEventListener("click", () => void runRemoval());
  // 採否を選ばなくても切り抜き結果のまま先へ進めるため、この計測は全利用者が通るわけではない。
  // ファネルの必須ステップとして扱うと、選ばずに進んだ利用者が離脱として数えられる
  $("#choose-removed").addEventListener("click", () => {
    analytics.track("cutout_selected", { choice: "removed" });
    dispatch({ type: "useOriginalChosen", useOriginal: false });
  });
  $("#choose-original").addEventListener("click", () => {
    analytics.track("cutout_selected", { choice: "original" });
    dispatch({ type: "useOriginalChosen", useOriginal: true });
  });

  $<HTMLInputElement>("#telop-input").addEventListener("input", (e) => {
    dispatch({
      type: "slotEdited",
      slot: "telop",
      edit: { text: (e.target as HTMLInputElement).value },
    });
  });
  $<HTMLInputElement>("#viewers-input").addEventListener("input", (e) => {
    const value = Number((e.target as HTMLInputElement).value);
    if (Number.isFinite(value)) {
      dispatch({ type: "slotEdited", slot: "viewers", edit: { value } });
    }
  });
  $("#undo-btn").addEventListener("click", () => {
    ({ state, history } = undo(state, history));
    render();
  });
  $("#redo-btn").addEventListener("click", () => {
    ({ state, history } = redo(state, history));
    render();
  });

  $("#download-btn").addEventListener("click", () => {
    if (!outputBlob) return;
    downloadBlob(outputBlob);
    analytics.track("download");
  });
  $("#share-btn").addEventListener("click", () => {
    if (!outputBlob) return;
    void shareFile(outputBlob).then((result) => {
      // キャンセル・失敗を共有到達として数えない
      if (result === "shared") analytics.track("share", { method: "os_share_sheet" });
    });
  });

  window.addEventListener("resize", () => {
    if (state.step === 4) renderStage();
  });

  // 編集内容はページ内にのみ存在するため、素材取り込み後の離脱時は確認する
  window.addEventListener("beforeunload", (e) => {
    if (state.main !== null || state.subject !== null) {
      e.preventDefault();
    }
  });

  // モデルダウンロードの進捗を表示する（不確定な待ちにしない）
  onModelProgress(({ loaded, total }) => {
    if (state.removal.status !== "loading") return;
    const percent = Math.min(100, Math.round((loaded / total) * 100));
    $("#removal-progress").hidden = false;
    $("#removal-progress-bar").style.width = `${percent}%`;
    $("#removal-progress-label").textContent =
      `${percent}%（${Math.round(loaded / 1024 / 1024)} / ${Math.round(total / 1024 / 1024)}MB）`;
  });

  // 素材取り込みステップの表示中に推論モデルを先読みし、切り抜きの待ち時間を短縮する
  void preloadModel().catch(() => {
    // 先読みの失敗はここでは無視する（切り抜き実行時に再試行・案内する）
  });

  render();
  // 初期表示のステップは goTo を通らないため、ここで計測する
  analytics.track("step_view", { step_number: state.step });
}
